import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { AI_QUERY } from '../../graphql/ai/queries';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft, IconMinus } from '@tabler/icons-react';
import { canonicalFeatureFlags } from '@/utils/featureFlags';
import { useLocation } from 'react-router-dom';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestedActions?: Array<{ label: string; action: string }>;
}

const SparkleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
    <path d="M5 3v4" />
    <path d="M3 5h4" />
    <path d="M19 17v4" />
    <path d="M17 19h4" />
  </svg>
);

const SendIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function AIChat({
  embedded = false,
  onBack,
  onMinimize,
  onClose,
}: {
  embedded?: boolean;
  /** Back to the widget's menu, offered only when the panel sits inside it. */
  onBack?: () => void;
  /** Put the panel away without losing the thread, same as the SP app. */
  onMinimize?: () => void;
  onClose?: () => void;
}) {
  const { t } = useTranslation();

  // Switched off, the assistant still opens from its button and says when it
  // is coming, rather than vanishing: a client who has seen the button before
  // would otherwise be left wondering what happened to it. Nothing is sent
  // while it is off.
  const isAiDisabled = canonicalFeatureFlags.isAiAssistantDisabled();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [sendQuery, { loading }] = useLazyQuery(AI_QUERY, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const response = data?.aiQuery;
      if (response?.__typename === 'AIQueryResponse') {
        setMessages(prev => [
          ...prev,
          {
            id: `ai_${Date.now()}`,
            role: 'assistant',
            content: response.message,
            timestamp: new Date(),
            suggestedActions: response.suggestedActions,
          },
        ]);
      } else if (response?.__typename === 'AIError') {
        setMessages(prev => [
          ...prev,
          {
            id: `err_${Date.now()}`,
            role: 'assistant',
            content: response.message || t('aiChatError'),
            timestamp: new Date(),
          },
        ]);
      }
    },
    onError: error => {
      setMessages(prev => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          role: 'assistant',
          content: error.message || t('aiChatError'),
          timestamp: new Date(),
        },
      ]);
    },
  });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (
    !embedded &&
    (location.pathname === '/signin' ||
      location.pathname === '/signup' ||
      location.pathname === '/invite' ||
      location.pathname.startsWith('/invite/'))
  ) {
    return null;
  }

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || loading || isAiDisabled) return;

    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInputValue('');

    sendQuery({
      variables: { query: text },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedAction = (action: string) => {
    if (isAiDisabled) return;
    setInputValue(action);
    setTimeout(() => {
      const userMsg: ChatMessage = {
        id: `user_${Date.now()}`,
        role: 'user',
        content: action,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMsg]);
      sendQuery({
        variables: { query: action },
      });
    }, 50);
    setInputValue('');
  };

  const formatMessage = (content: string) => {
    return content.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
  };

  return (
    <>
      {!embedded && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`fixed z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
            isOpen ? 'bg-gray-600 text-white' : 'bg-primary text-white'
          }`}
          style={{ bottom: '20px', right: '20px' }}
          aria-label={t('aiChatToggle')}
        >
          {isOpen ? <CloseIcon className="w-6 h-6" /> : <SparkleIcon className="w-6 h-6" />}
        </button>
      )}

      {/* Chat Window */}
      {(isOpen || embedded) && (
        <div
          className="fixed z-[9998] w-[380px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-12rem)] bg-white rounded-2xl shadow-2xl border border-ds-border flex flex-col overflow-hidden"
          style={{ bottom: '90px', right: '20px' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-primary text-white rounded-t-2xl flex-shrink-0">
            <SparkleIcon className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold">{t('aiChatTitle')}</h3>
              <p className="text-xs opacity-80">{t('aiChatSubtitle')}</p>
            </div>
            {/* Inside the widget the panel is one of several views, so it
                carries the same three actions the SP app gives it: back to the
                menu, out of the way, and gone. On its own there is nowhere to
                go back to and nothing behind it, so only the close remains —
                which is also what the SP app does. */}
            {embedded ? (
              <div className="flex items-center gap-1">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="p-1 hover:bg-white/20 rounded-full transition-colors"
                    aria-label={t('back')}
                    title={t('back')}
                  >
                    <IconArrowLeft size={18} stroke={2} />
                  </button>
                )}
                {onMinimize && (
                  <button
                    type="button"
                    onClick={onMinimize}
                    className="p-1 hover:bg-white/20 rounded-full transition-colors"
                    aria-label={t('minimize')}
                    title={t('minimize')}
                  >
                    <IconMinus size={18} stroke={2} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    onClose?.();
                  }}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label={t('close')}
                  title={t('close')}
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onClose?.();
                }}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label={t('close')}
                title={t('close')}
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Coming soon, in place of the messages, while the assistant is off */}
          {isAiDisabled ? (
            <div className="flex-1 min-h-0 flex items-center justify-center px-4 py-5">
              <div className="w-full flex flex-col items-center gap-2 text-center">
                <span className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center">
                  <SparkleIcon className="w-6 h-6" />
                </span>
                <span className="inline-flex items-center gap-1 rounded-xl bg-yellowContainer px-2.5 py-1 text-sm font-semibold text-onYellowContainer">
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-warningContainerBorder"
                    aria-hidden="true"
                  />
                  {t('aiChatComingSoon')}
                </span>
                <p className="text-lg font-semibold text-ds-text-primary px-4">
                  {t('aiChatAvailableSoon')}
                </p>
                <p className="text-sm text-ds-text-secondary">
                  {t('aiChatAvailableSoonDescription')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
              {messages.length === 0 && (
                <div className="text-center text-ds-text-secondary text-sm py-8">
                  <SparkleIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">{t('aiChatWelcome')}</p>
                  <p className="text-xs mt-1 opacity-70">{t('aiChatWelcomeHint')}</p>
                </div>
              )}

              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary text-white rounded-br-md'
                        : 'bg-ds-bg text-ds-text-primary rounded-bl-md'
                    }`}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatMessage(msg.content),
                      }}
                      className="whitespace-pre-wrap break-words [&_strong]:font-semibold"
                    />
                    {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-ds-border/50">
                        {msg.suggestedActions.map((action, i) => (
                          <button
                            key={i}
                            onClick={() => handleSuggestedAction(action.action)}
                            className="text-xs bg-white/90 text-primary px-2.5 py-1 rounded-full hover:bg-white transition-colors border border-primary/20"
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-ds-bg rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input — there is nothing to type into while the assistant is off */}
          {!isAiDisabled && (
            <div className="flex-shrink-0 border-t border-ds-border px-3 py-2.5 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('aiChatPlaceholder')}
                  disabled={loading}
                  rows={1}
                  className="flex-1 resize-none rounded-ds-card border border-ds-border px-3.5 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-50 max-h-24 overflow-y-auto"
                  style={{ minHeight: '40px' }}
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || loading}
                  className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-40 hover:bg-primary/90 transition-colors"
                >
                  <SendIcon />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
